import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import {
  cancelNotificationCampaign,
  createNotificationCampaign,
  deleteNotificationCampaign,
  getEmailPreview,
  getNotificationCampaign,
  getNotificationCampaigns,
  getNotificationRecipients,
  getSuppressions,
  previewAudience,
  resolveNotificationEmails,
  retractNotificationCampaign,
  createSuppression,
  deleteSuppression,
  sendNotificationCampaign,
  sendNotificationTest,
  updateNotificationCampaign,
} from '../api/adminNotifications.api';
import type {
  AudiencePreviewRequest,
  AudiencePreviewResponse,
  CampaignDto,
  CampaignInput,
  CampaignListParams,
  CampaignListResponse,
  CampaignRecipientsParams,
  CampaignRecipientsResponse,
  CreateSuppressionInput,
  EmailPreviewResponse,
  ResolveEmailsResponse,
  SendCampaignInput,
  SuppressionItem,
  SuppressionListParams,
  SuppressionListResponse,
  TestNotificationResponse,
} from '../types';

export const notificationQueryKeys = {
  all: ['admin', 'notifications'] as const,
  lists: () => [...notificationQueryKeys.all, 'campaigns'] as const,
  list: (params: CampaignListParams) => [...notificationQueryKeys.lists(), params] as const,
  detail: (publicId?: string) => [...notificationQueryKeys.all, 'campaign', publicId] as const,
  audiencePreview: (request?: AudiencePreviewRequest) =>
    [...notificationQueryKeys.all, 'audience-preview', request] as const,
  emailPreview: (publicId?: string, locale?: 'vi' | 'en') =>
    [...notificationQueryKeys.detail(publicId), 'email-preview', locale] as const,
  recipients: (publicId?: string, params?: CampaignRecipientsParams) =>
    [...notificationQueryKeys.detail(publicId), 'recipients', params] as const,
  suppressions: () => [...notificationQueryKeys.all, 'suppressions'] as const,
  suppressionList: (params: SuppressionListParams) =>
    [...notificationQueryKeys.suppressions(), params] as const,
};

export const useNotificationCampaigns = (
  params: CampaignListParams
): UseQueryResult<CampaignListResponse, Error> => {
  return useQuery({
    queryKey: notificationQueryKeys.list(params),
    queryFn: () => getNotificationCampaigns(params),
    staleTime: 10000,
  });
};

export const useNotificationCampaign = (
  publicId?: string,
  refetchInterval?: number | false
): UseQueryResult<CampaignDto, Error> => {
  return useQuery({
    queryKey: notificationQueryKeys.detail(publicId),
    queryFn: () => getNotificationCampaign(publicId as string),
    enabled: Boolean(publicId),
    refetchInterval,
  });
};

export const useAudiencePreview = (
  request?: AudiencePreviewRequest,
  enabled = true,
  staleTime = 5000
): UseQueryResult<AudiencePreviewResponse, Error> => {
  return useQuery({
    queryKey: notificationQueryKeys.audiencePreview(request),
    queryFn: () => previewAudience(request as AudiencePreviewRequest),
    enabled: enabled && Boolean(request),
    staleTime,
  });
};

export const useNotificationRecipients = (
  publicId?: string,
  params?: CampaignRecipientsParams
): UseQueryResult<CampaignRecipientsResponse, Error> => {
  return useQuery({
    queryKey: notificationQueryKeys.recipients(publicId, params),
    queryFn: () => getNotificationRecipients(publicId as string, params || {}),
    enabled: Boolean(publicId),
    staleTime: 5000,
  });
};

export const useEmailPreview = (
  publicId?: string,
  locale?: 'vi' | 'en',
  enabled = true
): UseQueryResult<EmailPreviewResponse, Error> => {
  return useQuery({
    queryKey: notificationQueryKeys.emailPreview(publicId, locale),
    queryFn: () => getEmailPreview(publicId as string, locale),
    enabled: enabled && Boolean(publicId),
    staleTime: 5000,
  });
};

export const useSuppressions = (
  params: SuppressionListParams
): UseQueryResult<SuppressionListResponse, Error> => {
  return useQuery({
    queryKey: notificationQueryKeys.suppressionList(params),
    queryFn: () => getSuppressions(params),
    staleTime: 10000,
  });
};

export const useResolveNotificationEmails = (): UseMutationResult<
  ResolveEmailsResponse,
  Error,
  string[]
> => {
  return useMutation({ mutationFn: resolveNotificationEmails });
};

export const useCreateNotificationCampaign = (): UseMutationResult<
  CampaignDto,
  Error,
  CampaignInput
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createNotificationCampaign,
    onSuccess: (campaign) => {
      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.lists() });
      queryClient.setQueryData(notificationQueryKeys.detail(campaign.publicId), campaign);
    },
  });
};

export const useUpdateNotificationCampaign = (): UseMutationResult<
  CampaignDto,
  Error,
  { publicId: string; data: Partial<CampaignInput> }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, data }) => updateNotificationCampaign(publicId, data),
    onSuccess: (campaign) => {
      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.lists() });
      queryClient.setQueryData(notificationQueryKeys.detail(campaign.publicId), campaign);
    },
  });
};

export const useDeleteNotificationCampaign = (): UseMutationResult<
  { deleted: true },
  Error,
  string
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteNotificationCampaign,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationQueryKeys.lists() }),
  });
};

export const useSendNotificationTest = (): UseMutationResult<
  TestNotificationResponse,
  Error,
  { publicId: string; emails?: string[] }
> => useMutation({ mutationFn: ({ publicId, emails }) => sendNotificationTest(publicId, emails) });

export const useCreateSuppression = (): UseMutationResult<
  SuppressionItem,
  Error,
  CreateSuppressionInput
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSuppression,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.suppressions() });
    },
  });
};

export const useDeleteSuppression = (): UseMutationResult<{ deleted: true }, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSuppression,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.suppressions() });
    },
  });
};

export const useSendNotificationCampaign = (): UseMutationResult<
  CampaignDto,
  Error,
  { publicId: string; data: SendCampaignInput }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ publicId, data }) => sendNotificationCampaign(publicId, data),
    onSuccess: (campaign) => {
      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.lists() });
      queryClient.setQueryData(notificationQueryKeys.detail(campaign.publicId), campaign);
    },
  });
};

export const useCancelNotificationCampaign = (): UseMutationResult<CampaignDto, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cancelNotificationCampaign,
    onSuccess: (campaign) => {
      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.lists() });
      queryClient.setQueryData(notificationQueryKeys.detail(campaign.publicId), campaign);
    },
  });
};

export const useRetractNotificationCampaign = (): UseMutationResult<CampaignDto, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: retractNotificationCampaign,
    onSuccess: (campaign) => {
      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.lists() });
      queryClient.setQueryData(notificationQueryKeys.detail(campaign.publicId), campaign);
    },
  });
};
